import React from "react";

const NUMBERED_RE = /^(?:step\s*)?(\d+)[\.\)\:]\s+(.+)$/i;
const BULLET_RE = /^[-•*]\s+(.+)$/;
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function renderInline(text) {
  const raw = String(text || "");
  if (!raw) return null;

  const nodes = [];
  let last = 0;
  let match;
  let key = 0;
  INLINE_RE.lastIndex = 0;

  while ((match = INLINE_RE.exec(raw)) !== null) {
    if (match.index > last) {
      nodes.push(raw.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(
        <strong key={`b-${key++}`} className="faq-ask-hl">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(
        <code key={`c-${key++}`} className="faq-ask-code">
          {token.slice(1, -1)}
        </code>
      );
    } else {
      nodes.push(token);
    }
    last = match.index + token.length;
  }

  if (last < raw.length) {
    nodes.push(raw.slice(last));
  }

  return nodes.length ? nodes : raw;
}

function parseBlocks(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const numbered = trimmed.match(NUMBERED_RE);
    if (numbered) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (!t) {
          // Allow blank lines inside a step list only if next is still numbered
          let j = i + 1;
          while (j < lines.length && !lines[j].trim()) j += 1;
          if (j < lines.length && NUMBERED_RE.test(lines[j].trim())) {
            i = j;
            continue;
          }
          break;
        }
        const m = t.match(NUMBERED_RE);
        if (!m) break;
        items.push(m[2].trim());
        i += 1;
      }
      if (items.length) {
        blocks.push({ type: "steps", items });
        continue;
      }
    }

    const bullet = trimmed.match(BULLET_RE);
    if (bullet) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (!t) break;
        const m = t.match(BULLET_RE);
        if (!m) break;
        items.push(m[1].trim());
        i += 1;
      }
      if (items.length) {
        blocks.push({ type: "bullets", items });
        continue;
      }
    }

    const para = [trimmed];
    i += 1;
    while (i < lines.length) {
      const t = lines[i].trim();
      if (!t) break;
      if (NUMBERED_RE.test(t) || BULLET_RE.test(t)) break;
      para.push(t);
      i += 1;
    }
    blocks.push({ type: "paragraph", text: para.join(" ") });
  }

  return blocks;
}

/**
 * Renders assistant answer text with numbered steps, bullets, and **highlights**.
 */
export default function FaqAskAnswerBody({ text }) {
  const blocks = parseBlocks(text);

  if (!blocks.length) {
    return <p>{String(text || "")}</p>;
  }

  return (
    <div className="faq-ask-answer">
      {blocks.map((block, idx) => {
        if (block.type === "steps") {
          return (
            <ol key={`s-${idx}`} className="faq-ask-steps">
              {block.items.map((item, itemIdx) => (
                <li key={`s-${idx}-${itemIdx}`}>
                  <span className="faq-ask-step-num" aria-hidden="true">
                    {itemIdx + 1}
                  </span>
                  <span className="faq-ask-step-body">{renderInline(item)}</span>
                </li>
              ))}
            </ol>
          );
        }

        if (block.type === "bullets") {
          return (
            <ul key={`u-${idx}`} className="faq-ask-bullets">
              {block.items.map((item, itemIdx) => (
                <li key={`u-${idx}-${itemIdx}`}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`p-${idx}`} className="faq-ask-answer-p">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}
